import { useMemo } from 'react';
import { OnSelectionChange, useSelection } from '@tanium/react-use-selection';
import {
  UseExpansionProps,
  UseExpansionReturnValue,
} from '../types/StackedRibbonListComponentTypes';

const noop = () => {};

/**
 * Stacked Ribbon List hook to manage expansion props in a controlled way.
 */
export const useExpansion = <T>({
  defaultExpansion: defaultExpansionProp,
  defaultExpansionMode: defaultExpansionModeProp,
  expansion: expansionProp,
  expansionMode: expansionModeProp,
  isExpandable: isExpandableProp,
  hideExpansionDisclosure = false,
  enableExpandAll = true,
  onExpansionChange = noop,
}: UseExpansionProps<T>): UseExpansionReturnValue<T> => {
  /**
   * Convert from selection change event to expansion state,
   * and set the new state.
   */
  const handleSelectionChange: OnSelectionChange<T> = useMemo(
    () => (selectionChangeEvent) => {
      onExpansionChange({
        expansion: selectionChangeEvent.selection,
        expansionMode: selectionChangeEvent.selectionMode,
      });
    },
    [onExpansionChange],
  );

  // Expansion logic is the same as selection; just alias the variables.
  const {
    isSelectable: isExpandable,
    selection: expansion,
    selectionMode: expansionMode,
    toggleSelection: toggleExpansion,
    isIdSelected: isIdExpanded,
    selectAll: expandAll,
    toggleMode,
    clearSelection: clearExpansion,
  } = useSelection({
    defaultSelection: defaultExpansionProp,
    defaultSelectionMode: defaultExpansionModeProp,
    selection: expansionProp,
    selectionMode: expansionModeProp,
    onSelectionChange: handleSelectionChange,
    isSelectable: isExpandableProp,
  });

  // These props are intended for an expandable component.
  // The other props are provided for consumer convenience.
  const expansionControlProps = {
    expansion,
    expansionMode,
    isExpandable: isExpandable ?? false,
    onExpansionChange,
    hideExpansionDisclosure,
    enableExpandAll,
  };

  return {
    toggleExpansion,
    isIdExpanded,
    expandAll,
    toggleMode,
    clearExpansion,
    expansionControlProps,
  };
};
